// Cross-device pinned-thread sync.
//
// Pinned threads are the user's curated short-list of channels surfaced at
// the top of the chat list. Mirrors Signal's PinnedThreadManager: a flat
// per-user set, capped at 8, replicated across the user's own linked
// devices via encrypted control messages on the same Double Ratchet
// session as user content.
//
// This is conceptually similar to but DISTINCT from `PinnedMessage*` —
// PinnedMessage pins a single *message inside a channel* (per-channel set,
// cap 50), while PinnedThread pins a *whole channel to the chat list*
// (per-user set, cap 8). The two use different sentinel prefixes so the
// codecs cannot confuse each other.
//
// Threat model — what the encrypted envelope guarantees:
//
//   1. Confidentiality of the pinned set: the server only sees an opaque
//      `kaether:e2ee:v1:` envelope and cannot enumerate which threads are
//      pinned.
//   2. No cross-channel redirection: `channelId` lives inside the *signed*
//      plaintext, so a hostile server cannot rebind a pin event from one
//      channel to another without invalidating the envelope signature.
//   3. No action flipping: `action` (pin / unpin) lives inside the signed
//      plaintext too — the server cannot turn a pin into an unpin.
//   4. Sender-bound: pin/unpin events are accepted only when the envelope
//      signature resolves to the local user's identity. Other users'
//      pin events are rejected at the call site (the envelope-signature
//      gate is enforced before `PinnedThreadStore.apply`).
//   5. Monotonic per-`(channelId, ts)`: stale events whose ts is at or
//      below the recorded lastEventTs for the same channelId are dropped,
//      including a stale unpin that would otherwise undo a fresher pin.
//   6. Bounded state: at most 8 pinned threads. Pinning a 9th evicts the
//      oldest by `pinnedTs` FIFO — protects the chat-list rendering
//      surface, mirroring Signal's policy.
//
// Wire format of the *plaintext that gets encrypted* (NOT the on-the-wire
// envelope, which is the standard `kaether:e2ee:v1:` form):
//
//   kaether:thread:pin:v1:<base64url(JSON)>
//
// JSON shape:
//   { "schema": 1, "channelId": "ch", "action": "pin" | "unpin",
//     "ts": 1234567890 }
//
// Ported from kaether_for_ios/Stoat/E2EE/PinnedThread.swift

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type PinnedThreadAction = "pin" | "unpin";

export interface PinnedThreadPayload {
  readonly schema: 1;
  readonly channelId: string;
  readonly action: PinnedThreadAction;
  /** Unix seconds (advisory; lives inside the *signed* plaintext so the
   *  server cannot forge this). */
  readonly ts: number;
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

export class PinnedThreadCodecError extends Error {
  readonly kind: "missingPrefix" | "malformed" | "unsupportedSchema" | "emptyChannelId";
  readonly schemaVersion?: number;

  constructor(
    kind: "missingPrefix" | "malformed" | "unsupportedSchema" | "emptyChannelId",
    detail?: string,
    schemaVersion?: number,
  ) {
    const msg =
      kind === "malformed"
        ? `malformed: ${detail ?? ""}`
        : kind === "unsupportedSchema"
          ? `unsupportedSchema(${schemaVersion})`
          : kind === "emptyChannelId"
            ? "emptyChannelId"
            : "missingPrefix";
    super(msg);
    this.name = "PinnedThreadCodecError";
    this.kind = kind;
    this.schemaVersion = schemaVersion;
  }
}

// ---------------------------------------------------------------------------
// Codec
// ---------------------------------------------------------------------------

const PREFIX = "kaether:thread:pin:v1:";
const CURRENT_SCHEMA = 1;

function base64UrlEncode(bytes: Uint8Array): string {
  let b64 = btoa(String.fromCharCode(...bytes));
  return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function base64UrlDecode(s: string): Uint8Array | null {
  let std = s.replace(/-/g, "+").replace(/_/g, "/");
  const pad = (4 - (std.length % 4)) % 4;
  std += "=".repeat(pad);
  try {
    const binary = atob(std);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  } catch {
    return null;
  }
}

export const PinnedThreadCodec = {
  prefix: PREFIX,

  isPinEvent(plaintext: string): boolean {
    return plaintext.startsWith(PREFIX);
  },

  encode(payload: PinnedThreadPayload): string {
    const json = JSON.stringify(payload);
    const bytes = new TextEncoder().encode(json);
    return PREFIX + base64UrlEncode(bytes);
  },

  decode(plaintext: string): PinnedThreadPayload {
    if (!plaintext.startsWith(PREFIX)) {
      throw new PinnedThreadCodecError("missingPrefix");
    }
    const b64 = plaintext.slice(PREFIX.length);
    const bytes = base64UrlDecode(b64);
    if (!bytes) {
      throw new PinnedThreadCodecError("malformed", "invalid base64url");
    }
    let parsed: unknown;
    try {
      parsed = JSON.parse(new TextDecoder().decode(bytes));
    } catch (e) {
      throw new PinnedThreadCodecError(
        "malformed",
        `json decode failed: ${e instanceof Error ? e.message : String(e)}`,
      );
    }
    if (typeof parsed !== "object" || parsed === null) {
      throw new PinnedThreadCodecError("malformed", "not an object");
    }
    const obj = parsed as Record<string, unknown>;
    if (obj["schema"] !== CURRENT_SCHEMA) {
      throw new PinnedThreadCodecError(
        "unsupportedSchema",
        undefined,
        typeof obj["schema"] === "number" ? obj["schema"] : -1,
      );
    }
    if (typeof obj["channelId"] !== "string" || obj["channelId"] === "") {
      throw new PinnedThreadCodecError("emptyChannelId");
    }
    if (obj["action"] !== "pin" && obj["action"] !== "unpin") {
      throw new PinnedThreadCodecError(
        "malformed",
        `invalid action: ${String(obj["action"])}`,
      );
    }
    if (typeof obj["ts"] !== "number") {
      throw new PinnedThreadCodecError("malformed", "ts must be a number");
    }
    return {
      schema: 1,
      channelId: obj["channelId"] as string,
      action: obj["action"] as PinnedThreadAction,
      ts: obj["ts"] as number,
    };
  },

  /**
   * Convenience: returns `null` for non-pin-thread events.
   * Throws `PinnedThreadCodecError` only for payloads that claimed to be
   * pin-thread events (i.e. prefix matched) but were malformed.
   */
  tryDecode(plaintext: string): PinnedThreadPayload | null {
    if (!plaintext.startsWith(PREFIX)) return null;
    return PinnedThreadCodec.decode(plaintext);
  },
} as const;
